// Dates and times in WIB (the backend's business day and Surat Jalan month are WIB).
import { LOCALE, TIME_ZONE, WIB_OFFSET } from '@constants/locale';

export const DAY_MS = 86_400_000;

const clock = new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: TIME_ZONE });
const dayMonth = new Intl.DateTimeFormat(LOCALE, { weekday: 'short', day: 'numeric', month: 'short', timeZone: TIME_ZONE });
const tf = new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit', timeZone: TIME_ZONE });
const df = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', timeZone: TIME_ZONE });
const dtf = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: TIME_ZONE });
const dtfDay = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', year: 'numeric', timeZone: TIME_ZONE });
const dtfDayLong = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'long', year: 'numeric', timeZone: TIME_ZONE });
const longDay = new Intl.DateTimeFormat(LOCALE, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: TIME_ZONE });
const yearOnly = new Intl.DateTimeFormat(LOCALE, { year: 'numeric', timeZone: TIME_ZONE });
const dayKey = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: TIME_ZONE });
// date-only values ("2026-10-24") are calendar dates: format them in UTC so no zone shifts the day
const calLong = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
const calDayMonth = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', timeZone: 'UTC' });
const calShort = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const calWeekdayDayMonth = new Intl.DateTimeFormat(LOCALE, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const calWeekdayDayMonthYear = new Intl.DateTimeFormat(LOCALE, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const calWeekdayLong = new Intl.DateTimeFormat(LOCALE, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
const calMonthYear = new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric', timeZone: 'UTC' });
const calFull = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
const calWeekdayShort = new Intl.DateTimeFormat(LOCALE, { weekday: 'short', timeZone: 'UTC' });
const calWeekdayName = new Intl.DateTimeFormat(LOCALE, { weekday: 'long', timeZone: 'UTC' });

const toDate = (s: string | Date) => (s instanceof Date ? s : new Date(s));
const calendar = (s: string) => {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

/** "10.40" */
export const time = (s: string | null | undefined) => (s ? tf.format(toDate(s)) : '–');
/** "10.40.05": the running clock. */
export const clockTime = (d: Date = new Date()) => clock.format(d);
/** "Sab, 24 Okt" */
export const shortDay = (d: Date = new Date()) => dayMonth.format(d);
/** "Sen, 24 Okt" for a calendar date, with the year ("Sen, 24 Okt 2025") when it is not the current WIB year. */
export const calendarWeekdayDay = (s: string) => (s.slice(0, 4) === wibYear() ? calWeekdayDayMonth : calWeekdayDayMonthYear).format(calendar(s));
/** "Senin, 24 Oktober 2026": a calendar date in full (a calendar day's accessible name). */
export const calendarDayLong = (s: string) => calWeekdayLong.format(calendar(s));
/** "Oktober 2026" for a calendar month ("2026-10"). */
export const calendarMonthTitle = (m: string) => calMonthYear.format(calendar(`${m}-01`));
/** Monday-first weekday names, short ("Sen" … "Min") and long ("Senin" … "Minggu"). 2024-01-01 was a Monday. */
export const WEEKDAYS = Array.from({ length: 7 }, (_, i) => {
  const d = new Date(Date.UTC(2024, 0, 1 + i));
  return { short: calWeekdayShort.format(d), long: calWeekdayName.format(d) };
});
/** "15 Okt", "1–15 Okt", "28 Sep – 3 Okt", "28 Des 2025 – 3 Jan 2026": a range of calendar dates, the year only when not this one. */
export function calendarRange(from: string, to: string): string {
  const thisYear = wibYear();
  const fmt = from.slice(0, 4) === thisYear && to.slice(0, 4) === thisYear ? calDayMonth : calShort;
  return from === to ? fmt.format(calendar(from)) : fmt.formatRange(calendar(from), calendar(to));
}
/** "1–3 November 2026": a range of calendar dates written in full, the year always said (a report's period). */
export const calendarRangeFull = (from: string, to: string) => (from === to ? calFull.format(calendar(from)) : calFull.formatRange(calendar(from), calendar(to)));
/** "24 Okt" for a calendar date ("2026-10-24"), e.g. chart axes. */
export const calendarDayMonth = (s: string) => calDayMonth.format(calendar(s));
/** "26 Okt 2026, 10.56 WIB": a moment in a sentence (the backend's formatWibTime says it the same way). */
export const wibTime = (s: string | null | undefined) => (s ? `${dtfDay.format(toDate(s))}, ${tf.format(toDate(s))} WIB` : '–');
/** "3 November 2026, 19.40 WIB": the full date, for a quiet meta line with room for it */
export const wibTimeLong = (s: string | null | undefined) => (s ? `${dtfDayLong.format(toDate(s))}, ${tf.format(toDate(s))} WIB` : '–');
/** "24 Okt 2026 10.40" */
export const dateTime = (s: string | null | undefined) => (s ? dtf.format(toDate(s)) : '–');
/** "26 Okt 2026, 10.56 WIB": a moment in user text, the zone said. */
export const wibDateTime = (s: string | null | undefined) => (s ? `${dtf.format(toDate(s))} WIB` : '–');
/** Calendar date ("24 Oktober 2026") for date columns and timestamps alike (a timestamp's WIB day, not its UTC one). */
export const date = (s: string | null | undefined) => (!s ? '–' : s.length === 10 ? calLong.format(calendar(s)) : dtfDayLong.format(toDate(s)));
export const dateShort = (s: string | null | undefined) => (!s ? '–' : s.length === 10 ? calShort.format(calendar(s)) : dtfDay.format(toDate(s)));
/** "Sabtu, 24 Oktober 2026" */
export const longDate = (d: Date = new Date()) => longDay.format(d);
/** "2026": the current year in WIB. */
export const wibYear = (d: Date = new Date()) => yearOnly.format(d);
/** YYYY-MM-DD of today (or of a timestamp) in WIB, for API date filters. */
export const wibDay = (d: Date | string = new Date()) => dayKey.format(toDate(d));
export const isToday = (s: string | null | undefined) => !!s && wibDay(s) === wibDay();

/** "10.40" today, "kemarin 10.40", "22 Okt 10.40" otherwise. */
export function when(s: string | null | undefined): string {
  if (!s) return '–';
  const d = toDate(s);
  const k = wibDay(d);
  if (k === wibDay()) return tf.format(d);
  if (k === wibDay(new Date(Date.now() - DAY_MS))) return `kemarin ${tf.format(d)}`;
  return `${df.format(d)} ${tf.format(d)}`;
}
/** "Hari ini 10.40" style for sentences. */
export const whenSentence = (s: string | null | undefined) => {
  const w = when(s);
  return /^\d/.test(w) && isToday(s) ? `hari ini ${w}` : w;
};

export function ago(s: string | null | undefined): string {
  if (!s) return 'belum pernah';
  const sec = Math.max(0, Math.round((Date.now() - toDate(s).getTime()) / 1000));
  if (sec < 45) return 'baru saja';
  if (sec < 3600) return `${Math.round(sec / 60)} menit lalu`;
  if (sec < 86400) return `${Math.round(sec / 3600)} jam lalu`;
  return `${Math.round(sec / 86400)} hari lalu`;
}

/** Live feeds: "baru saja", "12 mnt" within the hour, then the time ("10.40", "kemarin 10.40"). */
export function agoShort(s: string | null | undefined): string {
  if (!s) return '–';
  const sec = Math.max(0, Math.round((Date.now() - toDate(s).getTime()) / 1000));
  if (sec < 60) return 'baru saja';
  if (sec < 3600) return `${Math.floor(sec / 60)} mnt`;
  return when(s);
}

/** "08.02–08.10" or a single time when both ends are the same minute. */
export function timeSpan(from?: string | null, to?: string | null): string {
  if (!from) return '';
  const a = time(from);
  const b = to ? time(to) : a;
  return a === b ? a : `${a}–${b}`;
}

/** Whole days between a timestamp and now (never negative). */
export const daysSince = (s: string) => Math.max(0, Math.round((Date.now() - new Date(s).getTime()) / DAY_MS));

/** "5 dtk", "12 mnt", "3 jam": how long ago something happened, for live freshness lines (never negative). */
export function elapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s} dtk`;
  if (s < 3600) return `${Math.floor(s / 60)} mnt`;
  return `${Math.floor(s / 3600)} jam`;
}

/** 00:00 WIB of today (or of the WIB day of `d`), as epoch ms. */
export const wibDayStart = (d: Date | string = new Date()) => Date.parse(`${wibDay(d)}T00:00:00${WIB_OFFSET}`);
